/*
Expand/Collapse Caching Service

Custom charts persist their view-time state in `customVisualProps.clientState`,
a stringified ChartLocalState written through Answer__updateCustomVisualProps.

On an answer that state survives a filter change, because the answer edit
session outlives it. On a liveboard it does not. Applying a liveboard filter
bumps the pinboard session and reloads every viz under a new answer session
whose server-side state is spawned from the pinboard metadata (what the viz
looked like when it was pinned). TWO responses then write that pinned state
over the shared ChartViz entity: the Pinboard__loadContextBook reload, and the
getAnswer-rooted chart queries (GetChartWithData/GetChartWithoutData) the
remounting viz fires for the new session whenever the cache cannot satisfy
them. A pivot the viewer collapsed after pinning springs back fully expanded.

This cache holds only the `expandCollapse` block of that state, keyed by vizId,
which is stable across the session boundary. The shared merge
(upgradeOrInitVizClientState) — registered on both response paths — splices it
back into the reloaded viz. Only entries written from a liveboard viz are ever
restored (`isLiveboard`): on an answer the edit session's incoming value is
the truth, and stale page-lifetime entries must not override it.

Scope is deliberately just expand/collapse. The rest of clientState — column
widths, sort, legend, pagination — keeps its existing liveboard behaviour of
resetting on a filter change. A chart with no expandCollapse state (anything
that is not a pivot) is never cached and never touched.

It is page-lifetime only and is cleared when the liveboard unmounts or is
reset: this is transient view-time state, not saved liveboard state, so a
reload must start from the pinned metadata again.

structure: {
    [vizId]: { expandCollapse, answerSessionId, isLiveboard }
}
*/

import { create } from '@thoughtspot/logger';
import _ from 'lodash';

const logger = create('expand-collapse-caching-service');

interface CachedExpandCollapse {
    // The `expandCollapse` block of ChartLocalState, already parsed.
    expandCollapse: unknown;
    // Session the state was written under. Used to tell a genuine session
    // change (restore) from a re-merge within the same session (no-op).
    answerSessionId: string;
    // True when the write came from a viz rendered on a liveboard. Only
    // liveboard entries are restored; answer sessions keep the incoming
    // value as the truth.
    isLiveboard: boolean;
}

let expandCollapseCache: Record<string, CachedExpandCollapse> = {};

/**
 * Pulls the expandCollapse block out of a customVisualProps object.
 * Returns null when there is nothing to carry — clientState missing, not a
 * string, unparseable, or a chart that has no expand/collapse state at all.
 */
const extractExpandCollapse = (customVisualProps: unknown): unknown => {
    const clientState = (customVisualProps as any)?.clientState;
    if (typeof clientState !== 'string') return null;
    try {
        const parsed = JSON.parse(clientState);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return null;
        }
        return parsed.expandCollapse ?? null;
    } catch {
        return null;
    }
};

/**
 * Splices cached expandCollapse into a customVisualProps object, returning a
 * new object. An incoming clientState that is empty, unparseable, or not a
 * JSON object (seen in the wild on pinned liveboard metadata) is replaced by
 * a fresh clientState carrying only the expandCollapse block: the chart
 * cannot read such a value either — it falls back to defaults — so carrying
 * the viewer's state forward is strictly better than skipping the restore.
 */
const applyExpandCollapse = (
    customVisualProps: unknown,
    expandCollapse: unknown,
): unknown => {
    const clientState = (customVisualProps as any)?.clientState;

    let base: Record<string, unknown> = {};
    if (typeof clientState === 'string' && clientState !== '') {
        try {
            const parsed = JSON.parse(clientState);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                base = parsed;
            }
        } catch {
            logger.log(
                'Unreadable incoming clientState; rebuilding it around the',
                'restored expandCollapse block',
            );
        }
    }

    return {
        ...((customVisualProps as object) ?? {}),
        // Spreading before the override keeps expandCollapse in its original
        // position, so the serialized string only differs where it must.
        clientState: JSON.stringify({ ...base, expandCollapse }),
    };
};

const readFromExpandCollapseCache = (
    vizId: string,
): CachedExpandCollapse | null => {
    if (!vizId) return null;
    const cached = _.get(expandCollapseCache, [vizId], null);
    if (_.isNil(cached)) {
        logger.log('Expand/collapse cache miss for vizId:', vizId);
        return null;
    }
    // returns a copy so cache contents cannot be mutated by callers
    return _.cloneDeep(cached);
};

const writeToExpandCollapseCache = (
    vizId: string,
    customVisualProps: unknown,
    answerSessionId: string,
    isLiveboard = false,
) => {
    if (_.isNil(vizId) || vizId === '') {
        logger.log('Undefined vizId, skipping expand/collapse cache write');
        return;
    }
    const expandCollapse = extractExpandCollapse(customVisualProps);
    if (_.isNil(expandCollapse)) {
        // Not a pivot, or no expand/collapse state yet — nothing to carry.
        return;
    }
    logger.log(
        'Add/update expand/collapse cache for vizId: answerSessionId',
        vizId,
        answerSessionId,
    );
    // store a copy so later mutations of the chart's object do not leak in
    _.set(expandCollapseCache, [vizId], {
        expandCollapse: _.cloneDeep(expandCollapse),
        answerSessionId,
        isLiveboard,
    });
};

const evictExpandCollapseCache = (vizId: string) => {
    logger.log('Evicted expand/collapse cache for vizId', vizId);
    delete expandCollapseCache[vizId];
};

const clearExpandCollapseCache = () => {
    expandCollapseCache = {};
};

export {
    applyExpandCollapse,
    clearExpandCollapseCache,
    evictExpandCollapseCache,
    readFromExpandCollapseCache,
    writeToExpandCollapseCache,
};
